(() => {
  if (customElements.get('search-workbench')) return;
  const compact = value => String(value || '').toLowerCase().replace(/[^a-z0-9]/g, '');
  // The catalog stores sizes as 225/55-17 while shoppers also type 2255517.
  // Normalize size queries before native text search, which cannot match those forms.
  const tirePattern = /^\s*(?:P|LT|ST)?\s*(\d{3})[\s/\-]*(\d{2})[\s/RZ\-]*(\d{2}(?:\.5)?)(?:\s*(?:LT|C|XL))?\s*$/i;
  const tireSize = value => {
    const match = String(value || '').match(tirePattern);
    if (!match) return null;
    const width = Number(match[1]), aspect = Number(match[2]), rim = Number(match[3]);
    if (width < 135 || width > 405 || aspect < 20 || aspect > 90 || rim < 12 || rim > 30) return null;
    return `${match[1]}/${match[2]}R${match[3]}`;
  };
  class SearchWorkbench extends HTMLElement {
    connectedCallback() {
      if (this.events) return;
      this.events = new AbortController();
      const on = (node, event, fn) => node.addEventListener(event, fn, { signal: this.events.signal });
      this.tire = this.querySelector('[data-search-tire]');
      this.size = this.querySelector('[data-search-tire-size]');
      this.confirm = this.querySelector('[data-search-tire-confirm]');
      this.shopButton = this.querySelector('[data-search-tire-shop]');
      this.missing = this.querySelector('[data-search-tire-missing]');
      this.strings = JSON.parse(this.querySelector('[data-search-hub-strings]').textContent);
      on(this.querySelector('form.search-hub__form'), 'submit', event => {
        const query = new FormData(event.target).get('q').trim();
        const size = tireSize(query);
        if (size) { event.preventDefault(); this.shopTires(size); }
      });
      on(this, 'vehicle-lookup:shop', event => {
        const value = event.detail, url = new URL(this.dataset.rimsUrl, location.origin);
        if (value.bolt) { url.searchParams.set('rim_bolt', value.bolt); url.searchParams.set('rim_vehicle', value.label); }
        else if (value.factory) url.searchParams.set('rim_q', value.factory);
        url.hash = 'RimFinder'; location.assign(url.href);
      });
      on(this, 'vehicle-lookup:resolved', event => this.showTires(event.detail));
      on(this, 'vehicle-lookup:clear', () => {
        if (this.querySelector('vehicle-lookup')?.vehicle) return;
        this.tireVersion = (this.tireVersion || 0) + 1; this.tire.hidden = this.missing.hidden = true;
      });
      on(this.confirm, 'change', () => { this.shopButton.disabled = !this.confirm.checked || !this.size.value; });
      on(this.size, 'change', () => { this.confirm.checked = false; this.shopButton.disabled = true; });
      on(this.shopButton, 'click', () => { if (this.confirm.checked && this.size.value) this.shopTires(this.size.value); });
      const vehicle = this.querySelector('vehicle-lookup')?.vehicle;
      if (vehicle) this.showTires(vehicle);
      // Handle bookmarked or externally linked /search?q=2255517 URLs too.
      if (this.dataset.searchPerformed === 'true') {
        const size = tireSize(new URL(location.href).searchParams.get('q'));
        if (size) this.shopTires(size);
      }
    }
    disconnectedCallback() { this.events?.abort(); this.events = null; this.tireVersion = (this.tireVersion || 0) + 1; }
    shopTires(size) {
      const url = new URL(this.dataset.tiresUrl, location.origin);
      url.searchParams.set('tire_size', size);
      url.hash = 'tire-finder';
      location.assign(url.href);
    }
    async showTires(vehicle) {
      const version = this.tireVersion = (this.tireVersion || 0) + 1;
      this.tire.hidden = this.missing.hidden = true; this.confirm.checked = false; this.shopButton.disabled = true;
      try {
        const lookup = this.querySelector('vehicle-lookup');
        const oem = lookup?.fitment?.oem;
        if (oem?.options?.length) {
          this.size.replaceChildren();
          oem.options.forEach(option => {
            const matches = [...String(option.tire || '').matchAll(/(?:^|[^0-9])(\d{3})\/(\d{2})(?:ZR|R)(\d{2}(?:\.5)?)/gi)];
            if (matches.length !== 1) return;
            const match = matches[0], value = `${match[1]}/${match[2]}R${match[3]}`;
            const label = option.transmission === 'manual' ? lookup.t.manual : lookup.t.automatic;
            this.size.add(new Option(`${value} - ${lookup.t.factoryWheel}${oem.options.length > 1 ? ' - ' + label : ''}`, value));
          });
          this.tire.hidden = this.size.options.length === 0; this.missing.hidden = !this.tire.hidden;
          return;
        }
        this.reference ||= fetch(this.dataset.tireReference).then(response => { if (!response.ok) throw new Error('Reference'); return response.json(); }).catch(error => { this.reference = null; throw error; });
        const data = await this.reference;
        if (version !== this.tireVersion || !this.isConnected) return;
        const make = Object.keys(data).find(key => compact(key) === compact(vehicle.make));
        const models = data[make] || {};
        const model = Object.keys(models).find(key => compact(key) === compact(vehicle.model));
        let entry = models[model];
        if (Array.isArray(entry)) entry = entry.find(item => vehicle.year >= item.years[0] && vehicle.year <= item.years[1]);
        const choices = typeof entry === 'string' ? [[this.strings.base, entry]] : entry ? [[this.strings.base, entry.size || entry.default], ...Object.entries(entry.trims || {})] : [];
        this.size.replaceChildren();
        choices.forEach(([label, value]) => { if (typeof value === 'string' && tirePattern.test(value)) this.size.add(new Option(`${value} — ${label}`, value)); });
        this.tire.hidden = this.size.options.length === 0; this.missing.hidden = !this.tire.hidden;
      } catch (_) { if (version === this.tireVersion) this.missing.hidden = false; }
    }
  }
  customElements.define('search-workbench', SearchWorkbench);
})();
